/**
 * Product reviews from Okendo’s storefront REST API, rendered in the theme’s
 * review markup. Products with no published reviews, or a failed request,
 * show an empty state instead of placeholder or preview content. View all
 * opens a modal with the full list.
 *
 * Expects a <product-convert-reviews> root with a [data-review-template] and
 * [data-reviews-list]. Optional [data-reviews-empty], [data-reviews-modal],
 * and [data-product-rating] are updated from the product aggregate.
 */
(() => {
  const API = "https://api.okendo.io/v1/stores";
  const LIMIT = 25;
  const SCALE = 5;

  const parseCount = (value, fallback) => {
    const next = Number.parseInt(value, 10);
    return Number.isFinite(next) && next > 0 ? next : fallback;
  };

  const fillStars = (root, rating, labelTemplate) => {
    const fill = root.querySelector("[data-star-fill]");
    if (fill) {
      const clamped = Math.min(SCALE, Math.max(0, Number(rating) || 0));
      fill.style.width = `${(clamped / SCALE) * 100}%`;
    }

    const stars = root.querySelector("[data-star-rating]");
    if (stars && labelTemplate) {
      stars.setAttribute(
        "aria-label",
        labelTemplate.replaceAll("__RATING__", String(rating ?? 0)),
      );
    }
  };

  const ratingFromAggregate = (aggregate, reviews) => {
    const count =
      Number(aggregate?.reviewCount) ||
      Number(aggregate?.ratingAndReviewCount) ||
      reviews.length;
    const total =
      Number(aggregate?.reviewRatingValuesTotal) ||
      Number(aggregate?.ratingAndReviewValuesTotal) ||
      0;
    const rating = count > 0 ? total / count : 0;
    return { rating, count };
  };

  class ProductConvertReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.subscriberId = this.dataset.subscriberId || "";
      this.productId = this.dataset.productId || "";
      this.previewCount = parseCount(this.dataset.previewCount, 5);
      this.pagination = this.querySelector("[data-reviews-pagination]");
      this.pageButtons = this.querySelector("[data-reviews-page-buttons]");
      this.orderBy = this.dataset.orderBy || "date desc";
      this.ratingLabel = this.dataset.ratingLabel || "";
      this.list = this.querySelector("[data-reviews-list]");
      this.empty = this.querySelector("[data-reviews-empty]");
      this.template = this.querySelector("[data-review-template]");
      this.viewAll = this.querySelector("[data-reviews-view-all]");
      this.modal = this.querySelector("[data-reviews-modal]");
      this.modalList = this.querySelector("[data-reviews-modal-list]");
      this.modalEmpty = this.querySelector("[data-reviews-modal-empty]");
      this.modalClose = this.querySelector("[data-reviews-modal-close]");

      this.querySelectorAll("[data-reviews-write]").forEach((link) => {
        link.addEventListener("click", (event) => {
          const form = this.querySelector("review-form");
          if (typeof form?.open !== "function") return;
          event.preventDefault();
          if (this.modal?.open) {
            this.closeModal();
            form.querySelector("dialog").addEventListener("close", () => {
              this.openModal();
              link.focus();
            }, { once: true });
          }
          form.open();
        });
      });

      this.viewAll?.addEventListener("click", (event) => {
        event.preventDefault();
        this.openModal();
      });

      this.modalClose?.addEventListener("click", () => this.closeModal());

      this.modal?.addEventListener("click", (event) => {
        const rect = this.modal.getBoundingClientRect();
        const clickedInDialog =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!clickedInDialog) this.closeModal();
      });

      if (!this.subscriberId || !this.productId || !this.list || !this.template) {
        return;
      }

      this.abort = new AbortController();
      this.load();
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    async load() {
      const productPath = `${API}/${this.subscriberId}/products/shopify-${this.productId}`;
      const params = new URLSearchParams({
        limit: String(LIMIT),
        orderBy: this.orderBy,
      });

      try {
        const [reviewsRes, aggregateRes] = await Promise.all([
          fetch(`${productPath}/reviews?${params}`, { signal: this.abort.signal }),
          fetch(`${productPath}/review_aggregate`, { signal: this.abort.signal }),
        ]);

        if (!reviewsRes.ok) throw reviewsRes;

        const reviewsPayload = await reviewsRes.json();
        const reviews = Array.isArray(reviewsPayload.reviews)
          ? reviewsPayload.reviews
          : [];

        let aggregate = null;
        if (aggregateRes.ok) {
          const aggregatePayload = await aggregateRes.json();
          aggregate = aggregatePayload.reviewAggregate || null;
        }

        this.render(reviews);
        this.applyAggregate(aggregate, reviews);
      } catch (error) {
        if (error?.name === "AbortError") return;
        this.showEmpty();
      }
    }

    applyAggregate(aggregate, reviews) {
      if (aggregate) {
        const { rating, count } = ratingFromAggregate(aggregate, reviews);
        this.updateAggregate(rating, count);
        return;
      }

      if (reviews.length) {
        const total = reviews.reduce(
          (sum, review) => sum + (Number(review.rating) || 0),
          0,
        );
        this.updateAggregate(total / reviews.length, reviews.length);
        return;
      }

      this.updateAggregate(0, 0);
    }

    render(reviews) {
      const items = reviews
        .map((review) => this.createItem(review))
        .filter(Boolean);

      if (!items.length) {
        this.showEmpty();
        return;
      }

      this.items = items;
      this.renderPage(1);

      this.list.hidden = false;
      if (this.empty) this.empty.hidden = true;

      if (this.modalList) {
        this.modalList.replaceChildren(...items);
      }
      if (this.modalEmpty) this.modalEmpty.hidden = true;
    }

    renderPage(page, moveFocus = false) {
      const pageCount = Math.ceil(this.items.length / this.previewCount);
      this.page = Math.max(1, Math.min(pageCount, page));
      const start = (this.page - 1) * this.previewCount;
      this.list.replaceChildren(...this.items.slice(start, start + this.previewCount).map(item => item.cloneNode(true)));
      if (this.pagination && this.pageButtons) {
        this.pagination.hidden = pageCount <= 1;
        const makeButton = (label, target, disabled = false) => {
          const button = document.createElement("button");
          button.type = "button";
          button.textContent = label;
          button.disabled = disabled;
          button.addEventListener("click", () => this.renderPage(target, true));
          return button;
        };
        const buttons = [makeButton("Previous", this.page - 1, this.page === 1)];
        for (let number = 1; number <= pageCount; number++) {
          const button = makeButton(String(number), number);
          button.setAttribute("aria-label", `Review page ${number}`);
          if (number === this.page) button.setAttribute("aria-current", "page");
          buttons.push(button);
        }
        buttons.push(makeButton("Next", this.page + 1, this.page === pageCount));
        this.pageButtons.replaceChildren(...buttons);
      }
      if (moveFocus) {
        this.list.focus({ preventScroll: true });
        this.list.scrollIntoView({ behavior: "instant", block: "start" });
      }
    }

    createItem(review) {
      const body = String(review?.body || "").trim();
      if (!body) return null;

      const node = this.template.content.firstElementChild.cloneNode(true);
      const bodyEl = node.querySelector("[data-review-body]");
      const authorEl = node.querySelector("[data-review-author]");
      const verifiedEl = node.querySelector("[data-review-verified]");

      if (bodyEl) bodyEl.textContent = body;
      if (authorEl) {
        authorEl.textContent = String(review?.reviewer?.displayName || "").trim();
      }
      if (verifiedEl) verifiedEl.hidden = !review?.reviewer?.isVerified;

      fillStars(node, review.rating, this.ratingLabel);
      return node;
    }

    openModal() {
      if (!this.modal || typeof this.modal.showModal !== "function") return;
      if (this.modal.open) return;
      this.modal.showModal();
    }

    closeModal() {
      if (!this.modal?.open) return;
      this.modal.close();
    }

    showEmpty() {
      this.list?.replaceChildren();
      if (this.list) this.list.hidden = true;
      if (this.empty) this.empty.hidden = false;
      if (this.pagination) this.pagination.hidden = true;
      this.modalList?.replaceChildren();
      if (this.modalEmpty) this.modalEmpty.hidden = false;
      this.updateAggregate(0, 0);
    }

    updateAggregate(rating, count) {
      const summary = this.querySelector("[data-reviews-summary]");
      if (summary) {
        summary.hidden = !count;
        this.querySelector("[data-reviews-summary-rating]").textContent = `${Number(Number(rating).toFixed(1))} / 5`;
        this.querySelector("[data-reviews-summary-count]").textContent = "reviews";
      }
      const root = document.querySelector("[data-product-rating]");
      if (!root) return;

      const nextCount = Number(count) || 0;
      root.hidden = false;
      fillStars(root, nextCount > 0 ? rating : 0, this.ratingLabel);

      const countEl = root.querySelector("[data-product-rating-count]");
      if (countEl) countEl.textContent = `[ ${nextCount} ]`;
    }
  }

  if (!customElements.get("product-convert-reviews")) {
    customElements.define("product-convert-reviews", ProductConvertReviews);
  }
})();
